'use client';

import React from 'react';

import he from 'he';
import parse from 'html-react-parser';
import sanitizeHtml from 'sanitize-html';
import styled from 'styled-components';

import { sanitizeConfig } from './constants';
import { escapeGcodeParameters } from './gcodeUtils';
import { useParseOptions } from './replace';
import EditInAdminButton from '../EditInAdminButton';

import 'video-react/dist/video-react.css';

const Wrapper = styled.div`
  position: relative;
  max-width: 100%;
  width: 100% !important;

  img,
  iframe {
    max-width: 100%;
  }

  iframe {
    border: 0;
  }
`;

interface ParsedContentProps {
  article?: ArticleData;
  content?: string;
}

function ParsedContent({ article = null, content = '' }: ParsedContentProps) {
  const [isHovered, setIsHovered] = React.useState(false);
  const parseOptions = useParseOptions();

  const parsedContent = React.useMemo(() => {
    const rawContent = content || article?.content || '';
    if (!rawContent) return null;

    // Sanitize HTML first to preserve structure
    const sanitizedContent = sanitizeHtml(rawContent, sanitizeConfig);

    // Decode HTML entities
    const decodedContent = he.decode(sanitizedContent);

    // Escape G-code-like parameters (e.g., P<0-7>, R<1-3>, <base 10>) so they are not treated as HTML tags
    const finalContent = escapeGcodeParameters(decodedContent);

    // Parse to React elements
    return parse(finalContent, parseOptions);
  }, [content, article?.content, parseOptions]);

  return (
    <Wrapper onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <EditInAdminButton item={article} visible={isHovered} />
      {parsedContent}
    </Wrapper>
  );
}

export default React.memo(ParsedContent);
